import { useCallback, useMemo } from "react";

import Box from "@mui/material/Box";

import { useFormWithNotification } from "@covertree/hooks";
import { Button, Form, FormTextField } from "@covertree/ui";
import { InfoDisclaimerBox } from "@covertree/ui/content";
import { FORM_ERROR_DELAY_IN_MS } from "@covertree/consts";
import { Toast } from "@covertree/utils";

import { yupResolver } from "@hookform/resolvers/yup";
import { useCreatePolicyNoteMutation } from "@covertree/agent-portal-graphql";
import { PolicyNotesForm, defaultValues, validation } from "./form";

interface NewNoteFormProps {
  policyLocator: string;
  loading: boolean;
}

export function NewNoteForm({ policyLocator, loading }: NewNoteFormProps) {
  const form = useFormWithNotification({
    mode: "all",
    defaultValues,
    delayError: FORM_ERROR_DELAY_IN_MS,
    resolver: yupResolver(validation),
    reValidateMode: "onSubmit",
  });

  const {
    control,
    formState: { isValid },
    handleSubmit: submit,
    reset: resetForm,
  } = form;

  const [createPolicyNote, { loading: isMutationLoading }] =
    useCreatePolicyNoteMutation();

  const handleSubmit = useCallback(
    async ({ content }: PolicyNotesForm) => {
      try {
        await createPolicyNote({
          variables: {
            policyLocator,
            content,
          },
        });
        resetForm();
      } catch (e) {
        Toast.showError("Could not create new note. Please try again");
      }
    },
    [createPolicyNote, policyLocator]
  );

  const isLoading = useMemo(
    () => loading || isMutationLoading,
    [loading, isMutationLoading]
  );

  return (
    <Form onSubmit={submit(handleSubmit)}>
      <FormTextField
        hiddenLabel
        label={false}
        id="note"
        name="content"
        control={control}
        disabled={isLoading}
        placeholder="Start writing a note..."
      />
      <InfoDisclaimerBox
        dataTestId="disclaimer-box-policy-notes"
        fontSize={15}
        mb={3}
        mt={3}>
        Notes are for internal use only. Not monitored by Support. Visible to
        all users with policy access.
      </InfoDisclaimerBox>
      <Box sx={(theme) => ({ marginTop: theme.spacing(2) })}>
        <Button
          type="submit"
          loading={isMutationLoading}
          disabled={isLoading || !isValid}>
          Submit note
        </Button>
      </Box>
    </Form>
  );
}
